import { useCallback, useEffect, useMemo, useState } from 'react';
import { Organization, PaginatedResponse, Specialty } from '../shared/types';
import { apiDelete, apiGet, apiPost } from '../utils/api';
import { useUrlParams } from '../utils/url';
import { useAuth } from '../contexts/AuthContext';
import { PaginationParams, SortDirection } from '../components/table/useTableData';

export const useSpecialties = () => {
  const params = useUrlParams();
  const org_id = params.org_id;
  const { organizations } = useAuth();
  const organization = organizations.find((org: Organization) => org.id === Number(org_id));
  const defaultResponse = useMemo(() => ({
    items: [],
    total: 0,
      pageCount: 0,
      hasNextPage: false,
      currentPage: 0,
      pageSize: 0,
    }), []);
  const [ error, setError ] = useState<string | null>(null);

  const fetchSpecialties = useCallback(async (params: PaginationParams): Promise<PaginatedResponse<Specialty>> => {
    if (!organization) {
      setError('Organization not found');
      return defaultResponse;
    }

    try {
      const queryParams = {
        page: params.page.toString(),
        pageSize: params.pageSize.toString(),
        sortProperty: params.sortProperty || 'name',
        sortDirection: params.sortDirection || SortDirection.ASC,
      };

      const response = await apiGet(`/api/organizations/${organization.id}/specialties`, queryParams);
      const data = await response.json();

      if (!data.error) {
        return data;
      } else {
        setError(data.error);
        return defaultResponse;
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch specialties');
      return defaultResponse;
    }
  }, [organization, defaultResponse]);

  const addSpecialty = async (name: string): Promise<void> => {
    if (!organization) {
      setError('Organization not found');
      return;
    }

    try {
      const response = await apiPost(`/api/organizations/${organization.id}/specialties`, { name });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add specialty');
    }
  };

  const deleteSpecialty = async (name: string): Promise<void> => {
    if (!organization) {
      setError('Organization not found');
      return;
    }

    try {
      const response = await apiDelete(`/api/organizations/${organization.id}/specialties`, { name });
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete specialty');
    }
  };

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  }, [ error ]);

  return { fetchSpecialties, addSpecialty, deleteSpecialty, error };
};